import { Typography } from "@suraasa/placebo-ui"
import { AIToolsTabs } from "features/AItools"
import { useSuraasaPlusContext } from "features/SuraasaPlus/context"
import { useEffect, useMemo } from "react"
import Joyride, { Step } from "react-joyride"
import { NavigateFunction, useLocation } from "react-router-dom"
import routes from "routes"
import { BROWSER_STORAGE_KEYS } from "utils/constants"

import { SpnyTourEnum, useSuraasaPlusNewYear } from "../Context"
import { tourManager } from "../TourManager"
import { useTourContext } from "../TourProvider"
import OnboardingStep from "./OnboardingStep"
import ResumeAIToolsTour from "./ResumeAIToolsTour"
import { AI_TOOLS_ONBOARDING_TARGETS, OnboardingPhase } from "./types"

type Props = {
  onStart?: () => void
  onEnd?: () => void
  setActiveTab?: (tab: AIToolsTabs) => void
  navigate?: NavigateFunction
}

const AIToolsOnboarding = ({
  onStart,
  onEnd,
  setActiveTab,
  navigate,
}: Props) => {
  const location = useLocation()
  const { isSPNYUser } = useSuraasaPlusContext()
  const { step, setStep } = useTourContext()

  const { spnyAiToolsTour, isSpnyPlatformIntroCompleted } =
    useSuraasaPlusNewYear()

  const isAIToolsRoute = location.pathname.includes(routes.aiTools.home)

  const steps: Step[] = useMemo(
    () => [
      {
        target: `[data-tour="${AI_TOOLS_ONBOARDING_TARGETS.exploreTools}"]`,
        content: (
          <>
            <Typography variant="title3" className="mb-1">
              Your Advanced AI Tools
            </Typography>
            <Typography variant="smallBody" className="text-onSurface-600">
              Access all AI tools available to support your teaching and
              learning needs.
            </Typography>
          </>
        ),
        disableBeacon: true,
      },
      {
        target: `[data-tour="${AI_TOOLS_ONBOARDING_TARGETS.accessHistory}"]`,
        content: (
          <>
            <Typography variant="title3" className="mb-1">
              Access Your History
            </Typography>
            <Typography variant="smallBody" className="text-onSurface-600">
              Find all your previously created AI generations here.
            </Typography>
          </>
        ),
        disableBeacon: true,
      },
      {
        target: `[data-tour="${AI_TOOLS_ONBOARDING_TARGETS.takeALook}"]`,
        content: (
          <>
            <Typography variant="title3" className="mb-1">
              Take a look inside
            </Typography>
            <Typography variant="smallBody" className="text-onSurface-600">
              See how this tool works. Unlimited generations are included in
              your Suraasa Plus Membership.
            </Typography>
          </>
        ),
        disableBeacon: true,
      },
      {
        target: `[data-tour="${AI_TOOLS_ONBOARDING_TARGETS.describeActivity}"]`,
        content: (
          <>
            <Typography variant="title3" className="mb-1">
              Describe Your Activity
            </Typography>
            <Typography variant="smallBody" className="text-onSurface-600">
              Add the grade, duration and objective to generate your plan here.
            </Typography>
          </>
        ),
        disableBeacon: true,
      },
      {
        target: `[data-tour="${AI_TOOLS_ONBOARDING_TARGETS.generateActivity}"]`,
        content: (
          <>
            <Typography variant="title3" className="mb-1">
              Generate Activity Plan
            </Typography>
            <Typography variant="smallBody" className="text-onSurface-600">
              Use this button to generate an activity plan from your input.
            </Typography>
          </>
        ),
        disableBeacon: true,
      },
    ],
    []
  )

  const handleJoyrideFinish = () => {
    tourManager.completed(BROWSER_STORAGE_KEYS.spnyAiTools)
    setStep(null)
    onEnd?.()
  }

  const handleCloseOnboarding = () => {
    setStep(null)
    onEnd?.()
  }

  const handleContinue = () => {
    onStart?.()
    setStep(OnboardingPhase.joyride)
  }

  useEffect(() => {
    if (!isSPNYUser) return
    if (spnyAiToolsTour === SpnyTourEnum.completed) return
    if (!spnyAiToolsTour && isAIToolsRoute && isSpnyPlatformIntroCompleted) {
      const timeoutId = setTimeout(() => {
        setStep("joyride")
        tourManager.initiated(BROWSER_STORAGE_KEYS.spnyAiTools)
      }, 500)

      return () => clearTimeout(timeoutId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps, isSPNYUser, spnyAiToolsTour, isSpnyPlatformIntroCompleted])

  if (!isSPNYUser || spnyAiToolsTour === SpnyTourEnum.completed) return null

  return (
    <>
      <Joyride
        steps={steps}
        run={step === "joyride"}
        tooltipComponent={props => (
          <OnboardingStep
            toolTipRenderProps={props}
            switchTabsFunc={tab => {
              if (setActiveTab) setActiveTab(tab)
            }}
            navigateFunc={() => {
              navigate?.(routes.aiTools.activityPlanner)
            }}
            onComplete={handleJoyrideFinish}
          />
        )}
        continuous
        disableOverlayClose
        scrollOffset={120}
        scrollDuration={500}
        hideBackButton
        hideCloseButton
        // callback={handleJoyrideCallback}
      />

      <ResumeAIToolsTour
        onClose={handleCloseOnboarding}
        onContinue={handleContinue}
      />
    </>
  )
}

export default AIToolsOnboarding
